import { Button } from "@/components/ui/button";
import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import { useGetHealthPlan } from "@/features/health-plan/hooks/useGetHealthPlan";
import { useBloodTracker } from "@/features/holifya-tests/blood-test/hooks/useBloodTracker";
import { BloodTestTrackingCategory } from "@/features/holifya-tests/blood-test/types/BloodTestTrackingCategory";
import { useDnaTracker } from "@/features/holifya-tests/dna-test/hooks/useDnaTracker";
import { DnaTestTrackingCategory } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingCategory";
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus";
import { useGetMealPlans } from "@/features/meal-plan/hooks/useGetMealPlans";
import { useMembership } from "@/features/membership/hooks/useMembership";
import { SubType } from "@/features/membership/types/SubType";
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments";
import { HealthPlanPdfType } from "@/utils/api-objects/HealthPlan";
import { BloodTestTrackingStatus } from "@/utils/enums/TrackingStatus";
import { useConsultsStore } from "@/utils/stores/useConsultsStore";
import { useNavigate } from "react-router-dom";
import { ControlPageCardItem } from "../types/controlPageCardItem";
import { CardStatus } from "../types/controlPanelPageCardStatus";

export const useGetSectionItems = () => {
    const { dnaTracker, dnaOrderId, updateTracker: updateDnaTracker } = useDnaTracker()
    const { bloodTracker, updateTracker: updateBloodTracker } = useBloodTracker()
    const { getDnaTestCategory } = useDnaTracker()
    const { getBloodTestCategory } = useBloodTracker()
    const dnaTrackingCategory = getDnaTestCategory()
    const bloodTrackingCategory = getBloodTestCategory()
    const { data: documents } = useGetDocuments();
    const { healthPlans } = useGetHealthPlan()
    const { mealPlans } = useGetMealPlans()

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const filesUploaded =
        Array.isArray(documents) &&
        documents.some((doc) => new Date(doc.createdAt) > sixMonthsAgo);

    const newFilesUploaded = documents && documents.length > 1
        ? documents.some((doc, idx, arr) => {
            if (idx === 0) return false;
            return new Date(doc.createdAt).getTime() !== new Date(arr[0].createdAt).getTime();
        })
        : false;
    const { isHealthAssessmentCompleted } = useHealthAssessment()
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { consultsData } = useConsultsStore()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft
    const { specConsultsLeft, shouldPushBooking, lastSpecialistConsult } = useSpecialistConsult()
    const { nutriConsultsLeft, bookNutriAvailable, shouldPushNutritionistBooking, lastNutritionistConsult } = useNutritionistConsult()
    const membership = useMembership()
    const healthAssessmentFocus = membership === SubType.HEALTH ?
        (bloodTracker === BloodTestTrackingStatus.DeliveredToLab || bloodTracker === BloodTestTrackingStatus.ReportGenerated) :
        membership === SubType.HEALTH_DNA ?
            ((bloodTracker === BloodTestTrackingStatus.DeliveredToLab || bloodTracker === BloodTestTrackingStatus.ReportGenerated) && (dnaTracker === DnaTestTrackingStatus.Departed || dnaTracker === DnaTestTrackingStatus.DeliveredToLaboratory || dnaTracker === DnaTestTrackingStatus.StartingToExtract || dnaTracker === DnaTestTrackingStatus.StartingAnalysis || dnaTracker === DnaTestTrackingStatus.AnalysisDone || dnaTracker === DnaTestTrackingStatus.ReportGenerated)) :
            true
    const navigate = useNavigate()

    function getDnaSectionItems(): ControlPageCardItem[] {
        if (!dnaTracker) return []
        const shouldDnaTestBeFocused = bloodTracker ? (bloodTracker === BloodTestTrackingStatus.PickupRequested || bloodTracker === BloodTestTrackingStatus.DeliveredToLab || bloodTracker === BloodTestTrackingStatus.ReportGenerated) : false
        const homeItems: ControlPageCardItem[] = [
            {
                text: "📦 Hai ricevuto il kit?",
                percentage: dnaTracker === DnaTestTrackingStatus.Purchased ? 0 : 100,
                status: dnaTracker === DnaTestTrackingStatus.Purchased ? (shouldDnaTestBeFocused ? CardStatus.Focus : CardStatus.Standard) : CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => updateDnaTracker(DnaTestTrackingStatus.Delivered, dnaOrderId!)}>
                        Sì, ricevuto
                    </Button>
            },
            {
                text: "🧬 Effettua il test del DNA",
                percentage: (dnaTracker === DnaTestTrackingStatus.Purchased || dnaTracker === DnaTestTrackingStatus.Delivered) ? 0 : 100,
                status: dnaTracker === DnaTestTrackingStatus.Delivered ? (shouldDnaTestBeFocused ? CardStatus.Focus : CardStatus.Standard) :
                    dnaTracker === DnaTestTrackingStatus.Purchased ? CardStatus.Inactive :
                        CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/dna-test/tutorial")}>
                        Effettua
                    </Button>
            },
            {
                text: "🔐 Registra il Codice campione",
                percentage: (dnaTracker === DnaTestTrackingStatus.Purchased || dnaTracker === DnaTestTrackingStatus.Delivered || dnaTracker === DnaTestTrackingStatus.TestDone) ? 0 : 100,
                status: dnaTracker === DnaTestTrackingStatus.TestDone ? (shouldDnaTestBeFocused ? CardStatus.Focus : CardStatus.Standard) :
                    (dnaTracker === DnaTestTrackingStatus.Purchased || dnaTracker === DnaTestTrackingStatus.Delivered) ? CardStatus.Inactive :
                        CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/dna-test/register-swab-code")}>
                        Registra
                    </Button>
            },
            {
                text: "🚛 Richiedi il ritiro del campione",
                percentage: (dnaTracker === DnaTestTrackingStatus.Purchased || dnaTracker === DnaTestTrackingStatus.Delivered || dnaTracker === DnaTestTrackingStatus.TestDone || dnaTracker === DnaTestTrackingStatus.SampleRegistered) ? 0 : 100,
                status: dnaTracker === DnaTestTrackingStatus.SampleRegistered ? (shouldDnaTestBeFocused ? CardStatus.Focus : CardStatus.Standard) :
                    (dnaTracker === DnaTestTrackingStatus.Purchased || dnaTracker === DnaTestTrackingStatus.Delivered || dnaTracker === DnaTestTrackingStatus.TestDone) ? CardStatus.Inactive :
                        CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/dna-test/packing")}>
                        Richiedi
                    </Button>
            },
        ]

        // TODO [TM]: no focus if subhealthdna con un focus dopo
        const labItems: ControlPageCardItem[] = [
            {
                text: "📍 Arrivato in laboratorio",
                percentage: (dnaTracker === DnaTestTrackingStatus.PickupRequested || dnaTracker === DnaTestTrackingStatus.Departed) ? 0 : 100,
                status: dnaTracker === DnaTestTrackingStatus.Departed ? CardStatus.Focus : CardStatus.Completed,
                cheveronType: 'none',
            },
            {
                text: "🧪 Inizio estrazione",
                percentage: (dnaTracker === DnaTestTrackingStatus.StartingAnalysis || dnaTracker === DnaTestTrackingStatus.AnalysisDone) ? 100 : 0,
                status: dnaTracker === DnaTestTrackingStatus.Departed ? CardStatus.Inactive : dnaTracker === DnaTestTrackingStatus.StartingToExtract ? CardStatus.Focus : CardStatus.Completed,
                cheveronType: 'none',
            },
            {
                text: "🔬 In analisi",
                percentage: (dnaTracker === DnaTestTrackingStatus.AnalysisDone) ? 100 : 0,
                status: dnaTracker === DnaTestTrackingStatus.AnalysisDone ? CardStatus.Completed : (dnaTracker === DnaTestTrackingStatus.StartingAnalysis) ? CardStatus.Focus : CardStatus.Inactive,
                cheveronType: 'none',
            },
        ]

        const resultsReadyItems: ControlPageCardItem[] = [
            {
                text: "🧬 Visualizza risultati",
                percentage: null,
                status: CardStatus.Permanent,
                cheveronType: 'navigation',
                onClick: () => navigate("/dna-test")
            },
        ]

        if (dnaTracker === DnaTestTrackingStatus.PickupRequested) {
            homeItems.push({
                text: "🚛 Modifica il ritiro del campione",
                percentage: null,
                status: CardStatus.Focus,
                cheveronType: 'expand',
                expandedChildren:
                    <Button
                        size={'control-panel'} onClick={() => navigate("/dna-test/request-pickup")}>
                        Modifica il ritiro
                    </Button>
            });
        }
        if (dnaTrackingCategory === DnaTestTrackingCategory.Home) {
            return homeItems
        }
        if (dnaTrackingCategory === DnaTestTrackingCategory.InLaboratory) {
            return labItems
        }

        return resultsReadyItems
    }

    function getDnaSectionTitle(): string {
        const testHomeTitle = "Gestione del Test del DNA"
        const testInLabTitle = "Test del DNA in laboratorio"
        const testReadyTitle = "Test del DNA"
        if (dnaTrackingCategory === DnaTestTrackingCategory.Home) {
            return testHomeTitle
        }
        if (dnaTrackingCategory === DnaTestTrackingCategory.InLaboratory) {
            return testInLabTitle
        }

        return testReadyTitle
    }

    function getBloodSectionItems(): ControlPageCardItem[] {
        if (!bloodTracker) return []
        const homeItems: ControlPageCardItem[] = [
            {
                text: "📦 Hai ricevuto il kit?",
                percentage: bloodTracker === BloodTestTrackingStatus.Purchased ? 0 : 100,
                status: bloodTracker !== BloodTestTrackingStatus.Purchased ? CardStatus.Inactive : CardStatus.Focus,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => updateBloodTracker(BloodTestTrackingStatus.Delivered)}>
                        Sì, ricevuto
                    </Button>
            },
            {
                text: "🩸 Effettua l'analisi degli Aminoacidi",
                percentage: (bloodTracker === BloodTestTrackingStatus.Purchased || bloodTracker === BloodTestTrackingStatus.Delivered) ? 0 : 100,
                status: bloodTracker === BloodTestTrackingStatus.Delivered ? CardStatus.Focus : bloodTracker === BloodTestTrackingStatus.Purchased ? CardStatus.Inactive : CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/blood-test/tutorial")}>
                        Effettua
                    </Button>
            },
            {
                text: "🔐 Registra il Codice campione",
                percentage: (bloodTracker === BloodTestTrackingStatus.Purchased || bloodTracker === BloodTestTrackingStatus.Delivered || bloodTracker === BloodTestTrackingStatus.TestDone) ? 0 : 100,
                status: bloodTracker === BloodTestTrackingStatus.TestDone ? CardStatus.Focus : (bloodTracker === BloodTestTrackingStatus.Purchased || bloodTracker === BloodTestTrackingStatus.Delivered) ? CardStatus.Inactive : CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/blood-test/sample-register")}>
                        Registra
                    </Button>
            },
            {
                text: "🚛 Richiedi il ritiro del campione",
                percentage: (bloodTracker === BloodTestTrackingStatus.Purchased || bloodTracker === BloodTestTrackingStatus.Delivered || bloodTracker === BloodTestTrackingStatus.TestDone || bloodTracker === BloodTestTrackingStatus.SampleRegistered) ? 0 : 100,
                status: bloodTracker === BloodTestTrackingStatus.SampleRegistered ? CardStatus.Focus : (bloodTracker === BloodTestTrackingStatus.Purchased || bloodTracker === BloodTestTrackingStatus.Delivered || bloodTracker === BloodTestTrackingStatus.TestDone) ? CardStatus.Inactive : CardStatus.Completed,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/blood-test/packing")}>
                        Richiedi
                    </Button>
            },
        ]

        const labItems: ControlPageCardItem[] = [
            {
                text: "🔬 In analisi",
                percentage: 0,
                status: CardStatus.Inactive,
                cheveronType: 'none',
            },
        ]

        const resultsReadyItems: ControlPageCardItem[] = [
            {
                text: "🩸 Visualizza risultati",
                percentage: null,
                status: CardStatus.Permanent,
                cheveronType: 'navigation',
                onClick: () => navigate("/blood-test")
            },
        ]

        if (bloodTracker === BloodTestTrackingStatus.PickupRequested) {
            homeItems.push({
                text: "🚛 Modifica il ritiro del campione",
                percentage: null,
                status: dnaTracker === null ? CardStatus.Focus : CardStatus.Standard,
                cheveronType: 'expand',
                expandedChildren:
                    <Button
                        size={'control-panel'} onClick={() => navigate("/blood-test/request-pickup")}>
                        Modifica il ritiro
                    </Button>
            });
        }
        if (bloodTrackingCategory === BloodTestTrackingCategory.Home) {
            return homeItems
        }
        if (bloodTrackingCategory === BloodTestTrackingCategory.InLaboratory) {
            return labItems
        }

        return resultsReadyItems
    }

    function getBloodSectionTitle(): string {
        const testHomeTitle = "Gestione dell'analisi degli Aminoacidi"
        const testInLabTitle = "Analisi degli Aminoacidi in laboratorio"
        const testReadyTitle = "Analisi degli Aminoacidi"
        if (bloodTrackingCategory === BloodTestTrackingCategory.Home) {
            return testHomeTitle
        }
        if (bloodTrackingCategory === BloodTestTrackingCategory.InLaboratory) {
            return testInLabTitle
        }

        return testReadyTitle
    }

    function getClinicalHistoryItems(): ControlPageCardItem[] {
        const items: ControlPageCardItem[] = [
            {
                text: "📝 Rispondi all'Health Assessment",
                percentage: healthAssessmentCompleted ? 100 : 0,
                status: healthAssessmentCompleted ? CardStatus.Completed : healthAssessmentFocus ? CardStatus.Focus : CardStatus.Standard,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/health-assessment-home-page")}>
                        Rispondi
                    </Button>
            },
            {
                text: "📄 Carica i tuoi referti",
                percentage: filesUploaded ? 100 : 0,
                status: filesUploaded ? CardStatus.Completed : healthAssessmentCompleted ? CardStatus.Focus : CardStatus.Standard,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/reports")}>
                        Carica
                    </Button>
            },
        ]

        return items
    }

    // TODO [TM]: add the real check on the percentage
    function getCheckUpItems(): ControlPageCardItem[] {
        const items: ControlPageCardItem[] = [
            {
                text: "📝 Rispondi al questionario di check-up",
                percentage: 0,
                status: CardStatus.Focus,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/check-up-assessment-home-page")}>
                        Rispondi
                    </Button>
            },
            {
                text: "📄 Carica nuovi referti",
                percentage: newFilesUploaded ? 100 : 0,
                status: newFilesUploaded ? CardStatus.Completed : CardStatus.Standard,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/reports")}>
                        Carica
                    </Button>
            },
        ]

        return items
    }

    function getMedicalEquipeItems(): ControlPageCardItem[] {
        const items: ControlPageCardItem[] = []
        if (specialistConsultsLeft && specialistConsultsLeft > 0 && shouldPushBooking) {
            items.push({
                text: "👩‍⚕️️ Prenota consulto con medico",
                percentage: 0,
                status: CardStatus.Standard,
                cheveronType: 'expand',
                expandedChildren: (
                    <Button size={'control-panel'} onClick={() => navigate("/equipe/your-experts")}>
                        Prenota
                    </Button>
                ),
            });
        }
        if (specConsultsLeft.length > 0) {
            items.push({
                text: `${specConsultsLeft[0].expert.data.name} ${specConsultsLeft[0].expert.data.family_name}`,
                percentage: 100,
                status: CardStatus.Standard,
                expertRole: specConsultsLeft[0].expert.role,
                consult: specConsultsLeft[0],
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => window.open(specConsultsLeft[0].connectUrl)}>
                        Connettiti
                    </Button>
            });
        }

        if (lastSpecialistConsult) {
            items.push({
                text: `${lastSpecialistConsult.expert.data.name} ${lastSpecialistConsult.expert.data.family_name}`,
                percentage: 100,
                status: CardStatus.Inactive,
                expertRole: lastSpecialistConsult.expert.role,
                extraText: "Passato",
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => window.open(lastSpecialistConsult.connectUrl)}>
                        Connettiti
                    </Button>
            });
        }

        if (nutritionistConsultsLeft && nutritionistConsultsLeft > 0 && nutriConsultsLeft.length === 0 && shouldPushNutritionistBooking) {
            items.push({
                text: "👨‍⚕️️ Prenota consulto con nutrizionista",
                percentage: 0,
                status: bookNutriAvailable ? CardStatus.Focus : CardStatus.Inactive,
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => navigate("/equipe/your-experts")}>
                        Prenota
                    </Button>
            });
        }

        if (nutriConsultsLeft.length > 0) {
            items.push({
                text: `${nutriConsultsLeft[0].expert.data.name} ${nutriConsultsLeft[0].expert.data.family_name}`,
                percentage: 100,
                status: CardStatus.Standard,
                expertRole: nutriConsultsLeft[0].expert.role,
                consult: nutriConsultsLeft[0],
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => window.open(nutriConsultsLeft[0].connectUrl)}>
                        Connettiti
                    </Button>
            });
        }

        if (lastNutritionistConsult) {
            items.push({
                text: `${lastNutritionistConsult.expert.data.name} ${lastNutritionistConsult.expert.data.family_name}`,
                percentage: 100,
                status: CardStatus.Inactive,
                expertRole: lastNutritionistConsult.expert.role,
                extraText: "Passato",
                cheveronType: 'expand',
                expandedChildren:
                    <Button size={'control-panel'} onClick={() => window.open(lastNutritionistConsult.connectUrl)}>
                        Connettiti
                    </Button>
            });
        }

        return items
    }

    function getHealthPlanItems(): ControlPageCardItem[] {
        if (!healthPlans) return []
        const healthPlan = healthPlans[0]
        const items: ControlPageCardItem[] = [
            {
                text: "🌀 Consulta il tuo Health Plan",
                percentage: null,
                status: CardStatus.PermanentGoThrough,
                cheveronType: 'navigation',
                onClick: () => navigate("/health-plan")
            }
        ]
        const hasPrescriptions = healthPlan?.pdf_data?.some(pdf => pdf.type === HealthPlanPdfType.Prescriptions)
        if (hasPrescriptions) {
            items.push({
                text: "📄 Consulta le ultime prescrizioni",
                percentage: null,
                status: CardStatus.PermanentGoThrough,
                cheveronType: 'navigation',
                onClick: () => navigate(`/health-plan/pdfs?health_plan_id=${healthPlan.id}&type=${HealthPlanPdfType.Prescriptions}`)
            })
        }

        return items
    }

    function getMealPlanItems(): ControlPageCardItem[] {
        if (!mealPlans) return []
        const items: ControlPageCardItem[] = [
            {
                text: "🥙 Consulta il tuo Meal Plan",
                percentage: null,
                status: CardStatus.PermanentGoThrough,
                cheveronType: 'navigation',
                onClick: () => navigate("/meal-plan")
            }
        ]

        return items
    }

    return { getDnaSectionItems, getDnaSectionTitle, getBloodSectionItems, getBloodSectionTitle, getClinicalHistoryItems, getMedicalEquipeItems, getCheckUpItems, getHealthPlanItems, getMealPlanItems }
}
